import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./App.css";
import Wheel from "./components/Wheel.jsx";
import NamesPanel from "./components/NamesPanel.jsx";
import SpinButton from "./components/SpinButton.jsx";
import WinnerMenu from "./components/WinnerMenu.jsx";
import ItemsPanel from "./components/ItemsPanel.jsx";
import WinnersPanel from "./components/WinnersPanel.jsx";
import PostGiveaway from "./components/PostGiveaway.jsx";
import WheelStatusMessage from "./components/WheelStatusMessage.jsx";
import MuteButton from "./components/MuteButton.jsx";
import TitleBanner from "./components/TitleBanner.jsx";
import PublishWindow from "./components/PublishWindow.jsx";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import LoginForm from "./components/LoginForm.jsx";
import { AuthProvider, useAuth } from "./context/AuthContext";
import RegisterForm from "./components/RegisterForm.jsx";
import UnderwaterBackground from "./components/UnderwaterBackground";
import NavigationRow from "./components/NavigationRow.jsx";
import AdminPage from "./pages/AdminPage.jsx";
import GamePage from "./pages/GamePage.jsx";
import UserPage from "./pages/UserPage.jsx";
import PublicPage from "./pages/PublicPage.jsx";
import GuildMemberDetailPage from "./pages/GuildMemberDetailPage.jsx";
import SettingsButton from "./components/SettingsButton.jsx";
import SettingsModal from "./components/SettingsModal.jsx";
import AssignmentAmountModal from "./components/AssignmentAmountModal.jsx";
import GiveawayTitleModal from "./components/GiveawayTitleModal.jsx";
import ItemSelectWindow from "./components/ItemSelectWindow.jsx";
import NonparticipantSelectWindow from "./components/NonparticipantSelectWindow.jsx";
import FunPanel from "./components/FunPanel.jsx";
import MissionSetupWindow from "./components/MissionSetupWindow.jsx";
import SimulationTestWindow from "./components/SimulationTestWindow.jsx";
import { WheelSettingsProvider } from "./context/WheelSettingsContext";
import { useWheelSettings } from "./context/WheelSettingsContext";
import useGuildStashInventory from "./hooks/useGuildStashInventory";
import facade from "./services/qwof/apiFacade";
import { enrichItemsWithTradingPostPrices } from "./services/gw2API2/gw2API2";
import { createDefaultLabelFilters } from "./utils/itemFilters";
import DancingBlueQuaggan from "./images/Dancing Blue Quaggan.gif";
import DancingPinkQuaggan from "./images/Dancing Pink Quaggan.gif";
import HalloweenTheme from "./components/HalloweenTheme.jsx";
import FishSwimmer from "./components/FishSwimmer.jsx";
import FishSwimmer3D from "./components/FishSwimmer3D.jsx";

const cloneItems = (items) => items.map((item) => ({ ...item }));

const buildAssignedItemCounts = (winners) => {
  const counts = new Map();

  winners.forEach((winner) => {
    if (!winner.item?.id) return;
    counts.set(
      winner.item.id,
      (counts.get(winner.item.id) || 0) + (winner.item.count || 1),
    );
  });

  return counts;
};

const buildAvailableItems = (stashItems, assignedItemCounts) => {
  return stashItems.map((item) => ({
    ...item,
    count: Math.max(
      (item.count ?? 0) - (assignedItemCounts.get(item.id) || 0),
      0,
    ),
  }));
};

const ITEM_TYPES_WITH_AMOUNT_PICKER = new Set([
  "Container",
  "CraftingMaterial",
]);

const getUniqueParticipantNames = (names, winners) => {
  const seen = new Set();

  return [...names, ...winners.map((winner) => winner.name)]
    .map((name) => name.trim())
    .filter(Boolean)
    .filter((name) => {
      const key = name.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

function AppContent() {
  const { isLoggedIn, username } = useAuth();
  const { settings } = useWheelSettings();
  const [names, setNames] = useState([]);
  const [nonparticipants, setNonparticipants] = useState([]);
  const [isNonparticipantWindowOpen, setIsNonparticipantWindowOpen] = useState(false);
  const [input, setInput] = useState("");
  const [spinning, setSpinning] = useState(false);
  const [winnerIndex, setWinnerIndex] = useState(null);
  const [winnerSnapshot, setWinnerSnapshot] = useState({
    name: "",
    iconSrc: "",
    emojiSeed: "",
  });
  const [publishWheelSnapshot, setPublishWheelSnapshot] = useState({
    entries: [],
    winnerEntryIndex: -1,
    names: [],
    winnerIndex: -1,
    nameEmojiSeeds: [],
  });
  const [showWinnerMenu, setShowWinnerMenu] = useState(false);
  const [winnerNameRemovedFromList, setWinnerNameRemovedFromList] =
    useState(false);
  const [showPublishWindow, setShowPublishWindow] = useState(false);
  const [muted, setMuted] = useState(false);
  const [showRegisterLogin, setShowRegisterLogin] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showMissionSetup, setShowMissionSetup] = useState(false);
  const [showSimulationTest, setShowSimulationTest] = useState(false);
  const [nameEmojiVersions, setNameEmojiVersions] = useState({});
  const [nameIconByNormalizedName, setNameIconByNormalizedName] = useState({});
  const [winners, setWinners] = useState([]);
  const [assigningWinnerId, setAssigningWinnerId] = useState(null);
  const [pendingCraftingAssignment, setPendingCraftingAssignment] =
    useState(null);
  const [isAddItemWindowOpen, setIsAddItemWindowOpen] = useState(false);
  const [pendingGiveawayTitle, setPendingGiveawayTitle] = useState("");
  const [isGiveawayTitleModalOpen, setIsGiveawayTitleModalOpen] =
    useState(false);
  const [isSubmittingGiveaway, setIsSubmittingGiveaway] = useState(false);
  const [giveawayStatus, setGiveawayStatus] = useState({
    message: "",
    tone: "neutral",
  });
  const [wheelStatus, setWheelStatus] = useState({
    message: "",
    tone: "success",
    iconSrc: "",
    iconAlt: "",
  });
  const [itemLabelFilters, setItemLabelFilters] = useState(() => ({
    ...createDefaultLabelFilters(),
    Skins: 1,
    Tonics: 1,
  }));
  const nextWinnerIdRef = useRef(1);
  const wheelStatusTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isIqMember,
    loadError,
    loadedItems,
    loading,
    loadItems,
    setStashItems,
    showSpinner,
    stashItems,
  } = useGuildStashInventory({ isLoggedIn, username });

  useEffect(() => {
    let active = true;

    facade.getNonparticipants()
      .then((data) => {
        if (active) setNonparticipants(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error("Failed to load nonparticipants", err);
      });

    return () => {
      active = false;
    };
  }, []);

  const assignedItemCounts = buildAssignedItemCounts(winners);
  const availableItems = buildAvailableItems(stashItems, assignedItemCounts);
  const currentWinnerName = winnerSnapshot.name;
  const assigningWinner =
    winners.find((winner) => winner.id === assigningWinnerId) || null;
  const canSubmitGiveaway =
    isLoggedIn && facade.hasAnyRole(["ADMIN", "LEADER"]);
  const isCurrentWinnerInNames = Boolean(currentWinnerName) &&
    names.some(
      (entry) =>
        entry.trim().toLowerCase() === currentWinnerName.trim().toLowerCase(),
    );

  const getNameEmojiKey = (name, index) =>
    `${index}:${String(name || "")
      .trim()
      .toLowerCase()}`;

  const getNameEmojiSeed = (name, index) => {
    const key = getNameEmojiKey(name, index);
    const version = nameEmojiVersions[key] || 0;
    return `${key}:${version}`;
  };

  const rerollNameEmoji = (name, index) => {
    const key = getNameEmojiKey(name, index);
    setNameEmojiVersions((previous) => ({
      ...previous,
      [key]: (previous[key] || 0) + 1,
    }));
  };

  const getNameIconSrc = (name) => {
    const normalizedName = String(name || "")
      .trim()
      .toLowerCase();
    if (!normalizedName) return "";
    return nameIconByNormalizedName[normalizedName] || "";
  };

  const handlePopulateMissionSetup = ({ entries, useQuagganEmojis }) => {
    if (!Array.isArray(entries) || entries.length === 0) return;

    const nextNames = entries
      .map((entry) => String(entry?.name || "").trim())
      .filter(Boolean);

    if (!nextNames.length) return;

    const nextIconMap = entries.reduce((acc, entry) => {
      const normalizedName = String(entry?.name || "")
        .trim()
        .toLowerCase();
      if (!normalizedName) return acc;
      acc[normalizedName] = String(entry?.iconSrc || "");
      return acc;
    }, {});

    setNames(nextNames);
    setInput("");
    setWinnerIndex(null);
    setWinnerSnapshot({ name: "", iconSrc: "", emojiSeed: "" });
    setPublishWheelSnapshot({
      entries: [],
      winnerEntryIndex: -1,
      names: [],
      winnerIndex: -1,
      nameEmojiSeeds: [],
    });
    setShowWinnerMenu(false);
    setWinnerNameRemovedFromList(false);
    setWinners([]);
    setAssigningWinnerId(null);
    setPendingCraftingAssignment(null);
    setGiveawayStatus({ message: "", tone: "neutral" });
    setShowSimulationTest(false);

    if (useQuagganEmojis) {
      setNameIconByNormalizedName({});
      return;
    }

    setNameIconByNormalizedName(nextIconMap);
  };

  const showWheelStatus = (
    message,
    tone = "success",
    timeoutMs = 1600,
    options = {},
  ) => {
    if (wheelStatusTimeoutRef.current) {
      clearTimeout(wheelStatusTimeoutRef.current);
      wheelStatusTimeoutRef.current = null;
    }

    setWheelStatus({
      message,
      tone,
      iconSrc: options.iconSrc || "",
      iconAlt: options.iconAlt || "",
    });

    if (timeoutMs > 0) {
      wheelStatusTimeoutRef.current = setTimeout(() => {
        setWheelStatus({
          message: "",
          tone: "success",
          iconSrc: "",
          iconAlt: "",
        });
        wheelStatusTimeoutRef.current = null;
      }, timeoutMs);
    }
  };

  // When navigating to /register, default to showing the register form
  useEffect(() => {
    if (location.pathname === "/register") {
      setShowRegisterLogin(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    return () => {
      if (wheelStatusTimeoutRef.current) {
        clearTimeout(wheelStatusTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setNameEmojiVersions((previous) => {
      const validKeys = new Set(
        names.map((name, index) => getNameEmojiKey(name, index)),
      );
      const next = {};

      Object.entries(previous).forEach(([key, value]) => {
        if (validKeys.has(key)) {
          next[key] = value;
        }
      });

      return next;
    });
  }, [names]);

  const handleAddName = (e) => {
    e.preventDefault();
    if (input.trim()) {
      setNames([...names, input.trim()]);
      setInput("");
    }
  };

  const handleAddRandomNonparticipant = () => {
    if (nonparticipants.length === 0) return;
    const randomIndex = Math.floor(Math.random() * nonparticipants.length);
    const randomName = String(nonparticipants[randomIndex]?.name || "").trim();
    if (randomName) setNames((previous) => [...previous, randomName]);
  };

  const handleAddNonparticipants = (selectedNames) => {
    if (!selectedNames.length) return;
    setNames((previous) => [...previous, ...selectedNames]);
    setIsNonparticipantWindowOpen(false);
  };

  // Accepts spin options from SpinButton
  const handleSpin = ({ spins, duration }) => {
    setSpinning({ spins, duration });
    setWinnerIndex(null);
    setWinnerSnapshot({ name: "", iconSrc: "", emojiSeed: "" });
    setPublishWheelSnapshot({
      entries: [],
      winnerEntryIndex: -1,
      names: [],
      winnerIndex: -1,
      nameEmojiSeeds: [],
    });
    setShowWinnerMenu(false);
    setWinnerNameRemovedFromList(false);
  };

  const handleSpinEnd = (result) => {
    const winner = typeof result === "number" ? result : result?.rawIndex;
    const fallbackName = typeof result === "object" ? result?.name : "";
    const normalizedWinnerIndex =
      typeof winner === "number" && winner >= 0 ? winner : -1;
    const winningName =
      normalizedWinnerIndex >= 0
        ? names[normalizedWinnerIndex] ?? fallbackName ?? ""
        : fallbackName ?? "";
    const frozenNames = [...names];
    const frozenEmojiSeeds = frozenNames.map((name, index) =>
      getNameEmojiSeed(name, index),
    );
    const publishEntries = Array.isArray(result?.publishEntries)
      ? result.publishEntries
      : [];
    const winnerEntryIndex =
      typeof result?.winnerEntryIndex === "number" && result.winnerEntryIndex >= 0
        ? result.winnerEntryIndex
        : -1;
    setWinnerIndex(normalizedWinnerIndex >= 0 ? normalizedWinnerIndex : null);
    setWinnerSnapshot({
      name: winningName,
      iconSrc: getNameIconSrc(winningName),
      emojiSeed:
        normalizedWinnerIndex >= 0
          ? getNameEmojiSeed(winningName, normalizedWinnerIndex)
          : "",
    });
    setPublishWheelSnapshot({
      entries: publishEntries,
      winnerEntryIndex,
      names: frozenNames,
      winnerIndex: normalizedWinnerIndex,
      nameEmojiSeeds: frozenEmojiSeeds,
    });
    setWinnerNameRemovedFromList(false);
    setShowWinnerMenu(true);
    setSpinning(false);
  };

  const handleCloseWinnerMenu = () => {
    setShowWinnerMenu(false);
    setWinnerIndex(null);
    setWinnerNameRemovedFromList(false);
    setWinnerSnapshot({ name: "", iconSrc: "", emojiSeed: "" });
    setPublishWheelSnapshot({
      entries: [],
      winnerEntryIndex: -1,
      names: [],
      winnerIndex: -1,
      nameEmojiSeeds: [],
    });
  };

  const handleOpenPublishWindow = () => {
    const hasEntries = Array.isArray(publishWheelSnapshot.entries)
      ? publishWheelSnapshot.entries.length > 0
      : false;
    if (!currentWinnerName || (!hasEntries && !publishWheelSnapshot.names.length)) return;
    setShowPublishWindow(true);
  };

  const handleClosePublishWindow = () => {
    setShowPublishWindow(false);
  };

  const addWinnerEntry = (name, removeFromNames = true) => {
    const trimmedName = name.trim();
    if (!trimmedName) return;

    if (removeFromNames) {
      setNames((prev) => {
        const matchIndex = prev.findIndex(
          (entry) => entry.trim().toLowerCase() === trimmedName.toLowerCase(),
        );

        if (matchIndex === -1) return prev;

        return prev.filter((_, index) => index !== matchIndex);
      });
    }

    setWinners((prev) => [
      ...prev,
      {
        id: nextWinnerIdRef.current++,
        name: trimmedName,
        item: null,
      },
    ]);
  };

  const handleAssignWinnerItem = (winnerId, item, quantity = 1) => {
    setWinners((prev) =>
      prev.map((winner) =>
        winner.id === winnerId
          ? { ...winner, item: { ...item, count: quantity } }
          : winner,
      ),
    );
    setAssigningWinnerId(null);
    setPendingCraftingAssignment(null);
  };

  const handleRemoveWinnerItem = (winnerId) => {
    setWinners((prev) =>
      prev.map((winner) =>
        winner.id === winnerId ? { ...winner, item: null } : winner,
      ),
    );
  };

  const handleRemoveWinnerEntry = (winnerId) => {
    if (winnerId === assigningWinnerId) {
      setAssigningWinnerId(null);
    }
    setWinners((prev) => prev.filter((winner) => winner.id !== winnerId));
  };

  const handleStartAssignWinnerItem = (winnerId) => {
    setAssigningWinnerId(winnerId);
  };

  const handleCancelAssignWinnerItem = () => {
    setAssigningWinnerId(null);
    setPendingCraftingAssignment(null);
  };

  const handleAssignItemRequest = (item) => {
    if (!assigningWinner) return;

    const availableCount = item.count ?? 0;
    if (availableCount <= 0) return;

    const itemType = String(item?.type || "").trim();

    if (ITEM_TYPES_WITH_AMOUNT_PICKER.has(itemType) && availableCount > 1) {
      setPendingCraftingAssignment({
        item,
        quantity: availableCount,
        winnerId: assigningWinner.id,
      });
      return;
    }

    const quantity = ITEM_TYPES_WITH_AMOUNT_PICKER.has(itemType)
      ? availableCount
      : 1;
    handleAssignWinnerItem(assigningWinner.id, item, quantity);
  };

  const handleResetItems = () => {
    setStashItems(cloneItems(loadedItems));
    setWinners((prev) => prev.map((winner) => ({ ...winner, item: null })));
    setAssigningWinnerId(null);
    setPendingCraftingAssignment(null);
    setGiveawayStatus({ message: "", tone: "neutral" });
  };

  const handleRemoveItem = (itemId) => {
    const reservedCount = assignedItemCounts.get(itemId) || 0;
    setStashItems((prev) =>
      prev.flatMap((item) => {
        if (item.id !== itemId) return [item];
        if (reservedCount > 0) return [{ ...item, count: reservedCount }];
        return [];
      }),
    );
  };

  const handleDecrementItem = (itemId) => {
    setStashItems((prev) =>
      prev.flatMap((item) => {
        if (item.id !== itemId) return [item];

        const reservedCount = assignedItemCounts.get(item.id) || 0;
        if ((item.count ?? 0) <= reservedCount) {
          return [item];
        }

        const nextCount = (item.count ?? 0) - 1;
        if (nextCount <= 0 && reservedCount === 0) {
          return [];
        }

        return [{ ...item, count: Math.max(nextCount, reservedCount) }];
      }),
    );
  };

  const handleIncrementItem = (itemId) => {
    setStashItems((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, count: (item.count ?? 0) + 1 } : item,
      ),
    );
  };

  const applyTradingPostPricesToItems = async (newlyAddedItems = []) => {
    if (!Array.isArray(newlyAddedItems) || !newlyAddedItems.length) return;

    const priced = await enrichItemsWithTradingPostPrices(newlyAddedItems);
    if (!priced.length) return;

    const pricedById = new Map(priced.map((item) => [String(item.id), item]));

    setStashItems((previous) =>
      previous.map((item) => {
        const pricedItem = pricedById.get(String(item.id));
        return pricedItem
          ? { ...item, ...pricedItem, count: item.count }
          : item;
      }),
    );
  };

  const handleAddItemFromSelector = (selectedItem, quantity = 1) => {
    if (!selectedItem?.id) return;

    const normalizedCount = Math.max(1, Number(quantity) || 1);

    setStashItems((previous) => {
      const existingIndex = previous.findIndex(
        (item) => String(item.id) === String(selectedItem.id),
      );

      if (existingIndex === -1) {
        return [...previous, { ...selectedItem, count: normalizedCount }];
      }

      return previous.map((item, index) =>
        index === existingIndex
          ? {
              ...item,
              ...selectedItem,
              count: (item.count ?? 0) + normalizedCount,
            }
          : item,
      );
    });

    applyTradingPostPricesToItems([selectedItem]);

    setIsAddItemWindowOpen(false);
    showWheelStatus(
      `Successfully added ${selectedItem.name || "item"}`,
      "success",
      1600,
      {
        iconSrc: selectedItem.icon || "",
        iconAlt: selectedItem.name
          ? `${selectedItem.name} icon`
          : "Added item icon",
      },
    );
  };

  const handleAddItemsFromSelector = (selectedEntries = []) => {
    if (!Array.isArray(selectedEntries) || !selectedEntries.length) return;

    const addedItems = [];

    setStashItems((previous) => {
      let next = [...previous];

      selectedEntries.forEach((entry) => {
        const selectedItem = entry?.item;
        const quantity = entry?.quantity;

        if (!selectedItem?.id) return;
        addedItems.push(selectedItem);

        const normalizedCount = Math.max(1, Number(quantity) || 1);
        const existingIndex = next.findIndex(
          (item) => String(item.id) === String(selectedItem.id),
        );

        if (existingIndex === -1) {
          next = [...next, { ...selectedItem, count: normalizedCount }];
          return;
        }

        next = next.map((item, index) =>
          index === existingIndex
            ? {
                ...item,
                ...selectedItem,
                count: (item.count ?? 0) + normalizedCount,
              }
            : item,
        );
      });

      return next;
    });

    applyTradingPostPricesToItems(addedItems);

    setIsAddItemWindowOpen(false);
    showWheelStatus(
      `Successfully added ${selectedEntries.length} item type(s)`,
      "success",
      1800,
    );
  };

  // Spin again from winner menu
  const handleSpinAgain = () => {
    setShowWinnerMenu(false);
    setWinnerIndex(null);
    setWinnerNameRemovedFromList(false);
    setWinnerSnapshot({ name: "", iconSrc: "", emojiSeed: "" });
    setPublishWheelSnapshot({
      entries: [],
      winnerEntryIndex: -1,
      names: [],
      winnerIndex: -1,
      nameEmojiSeeds: [],
    });
    setTimeout(() => {
      // Pass empty object — Wheel will use settings context for defaults
      handleSpin({});
    }, 200);
  };

  const handleRemoveWinnerFromNames = () => {
    if (!currentWinnerName || winnerNameRemovedFromList) return;

    let didRemove = false;

    setNames((prev) => {
      const matchIndex = prev.findIndex(
        (entry) =>
          entry.trim().toLowerCase() === currentWinnerName.trim().toLowerCase(),
      );

      if (matchIndex === -1) return prev;

      didRemove = true;
      return prev.filter((_, idx) => idx !== matchIndex);
    });

    if (didRemove) {
      setWinnerNameRemovedFromList(true);
    }
  };

  const handleAddWinnerFromMenu = () => {
    if (!currentWinnerName) return;
    addWinnerEntry(currentWinnerName, !winnerNameRemovedFromList);
    if (!winnerNameRemovedFromList) {
      setWinnerNameRemovedFromList(true);
    }
  };

  const handleAddWinnerBackToNames = () => {
    if (!currentWinnerName || isCurrentWinnerInNames) return;
    setNames((prev) => [...prev, currentWinnerName]);
    setWinnerNameRemovedFromList(false);
  };

  const handleSubmitGiveaway = async () => {
    if (!canSubmitGiveaway || !username || isSubmittingGiveaway) return;

    const results = winners
      .filter((winner) => winner.item?.id)
      .map((winner) => ({
        participantName: winner.name,
        itemId: winner.item.id,
        quantity: winner.item.count || 1,
      }));

    if (results.length === 0) {
      setGiveawayStatus({
        message: "Assign at least one prize first.",
        tone: "error",
      });
      return;
    }

    const defaultTitle = `Giveaway ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString(
      [],
      {
        hour: "2-digit",
        minute: "2-digit",
      },
    )}`;
    setPendingGiveawayTitle(defaultTitle);
    setIsGiveawayTitleModalOpen(true);
  };

  const handleConfirmGiveawayTitle = async (title) => {
    setIsGiveawayTitleModalOpen(false);

    if (!title) {
      setGiveawayStatus({
        message: "A giveaway title is required.",
        tone: "error",
      });
      return;
    }

    const results = winners
      .filter((winner) => winner.item?.id)
      .map((winner) => ({
        participantName: winner.name,
        itemId: winner.item.id,
        quantity: winner.item.count || 1,
      }));

    if (results.length === 0) {
      setGiveawayStatus({
        message: "Assign at least one prize first.",
        tone: "error",
      });
      return;
    }

    setIsSubmittingGiveaway(true);
    setGiveawayStatus({ message: "Submitting...", tone: "neutral" });

    try {
      const participantNames = getUniqueParticipantNames(names, winners);
      const participants = participantNames.map((displayName) => ({
        displayName,
      }));

      await facade.postGame({
        createdByUsername: username,
        title,
        participants,
        results,
      });

      setGiveawayStatus({
        message: "Giveaway submitted.",
        tone: "success",
      });
    } catch (error) {
      const message =
        error?.fullError || error?.message || "Failed to submit giveaway.";
      setGiveawayStatus({
        message,
        tone: "error",
      });
    } finally {
      setIsSubmittingGiveaway(false);
    }
  };

  return (
    <div className="app-container">
      <UnderwaterBackground />
      {assigningWinner && (
        <div
          className="winner-assignment-overlay"
          onClick={handleCancelAssignWinnerItem}
        />
      )}
      <AssignmentAmountModal
        visible={!!pendingCraftingAssignment}
        itemName={pendingCraftingAssignment?.item?.name || "this item"}
        maxQuantity={pendingCraftingAssignment?.item?.count || 1}
        defaultQuantity={pendingCraftingAssignment?.quantity || 1}
        onCancel={() => setPendingCraftingAssignment(null)}
        onConfirm={(quantity) => {
          if (!pendingCraftingAssignment) return;
          handleAssignWinnerItem(
            pendingCraftingAssignment.winnerId,
            pendingCraftingAssignment.item,
            quantity,
          );
        }}
      />
      <GiveawayTitleModal
        visible={isGiveawayTitleModalOpen}
        defaultTitle={pendingGiveawayTitle}
        onCancel={() => setIsGiveawayTitleModalOpen(false)}
        onConfirm={handleConfirmGiveawayTitle}
      />
      <ItemSelectWindow
        visible={isAddItemWindowOpen}
        isLoggedIn={isLoggedIn}
        mode="item"
        selectionMode="basket"
        participants={[]}
        selectedParticipantId=""
        selectedItemId={null}
        onClose={() => setIsAddItemWindowOpen(false)}
        onSelectParticipant={() => {}}
        onSelectItem={handleAddItemFromSelector}
        onFinishSelection={handleAddItemsFromSelector}
      />
      <NonparticipantSelectWindow
        visible={isNonparticipantWindowOpen}
        nonparticipants={nonparticipants}
        onClose={() => setIsNonparticipantWindowOpen(false)}
        onFinishSelection={handleAddNonparticipants}
      />
      <Routes>
        <Route
          path="/"
          element={
            <>
              <div className={`main-layout${assigningWinner ? " winner-assignment-active" : ""}`}>
                <div className="side-panel left-panel">
                  <ItemsPanel
                    isLoggedIn={isLoggedIn}
                    username={username}
                    isIqMember={isIqMember}
                    items={availableItems}
                    labelFilters={itemLabelFilters}
                    setLabelFilters={setItemLabelFilters}
                    loadError={loadError}
                    loading={loading}
                    showSpinner={showSpinner}
                    assignmentMode={!!assigningWinner}
                    assignmentWinnerName={assigningWinner?.name || ""}
                    onRemoveItem={handleRemoveItem}
                    onDecrementItem={handleDecrementItem}
                    onIncrementItem={handleIncrementItem}
                    onAssignItem={
                      assigningWinner ? handleAssignItemRequest : undefined
                    }
                    onResetItems={handleResetItems}
                    onUpdateItems={loadItems}
                    onAddItemRequest={() => setIsAddItemWindowOpen(true)}
                    onNotify={showWheelStatus}
                  />
                </div>
                <div className="center-panel">
                  <TitleBanner
                    title="Quaggan Wheel"
                    subtitle="Fortune"
                    leftGif={DancingPinkQuaggan}
                    rightGif={DancingBlueQuaggan}
                  />
                  <div
                    className="wheel-wrapper"
                    style={{ "--wheel-scale": settings.wheelScale }}
                  >
                    <Wheel
                      names={names}
                      getNameEmojiSeed={getNameEmojiSeed}
                      getNameIconSrc={getNameIconSrc}
                      spinning={spinning}
                      onSpinEnd={handleSpinEnd}
                      winnerIndex={winnerIndex}
                      setWinnerIndex={setWinnerIndex}
                      showWinnerMenu={showWinnerMenu}
                      muted={muted}
                    />
                    {names.length > 0 ? (
                      <SpinButton
                        onSpin={handleSpin}
                        disabled={!!spinning || showWinnerMenu}
                      />
                    ) : null}
                  </div>
                  <WheelStatusMessage
                    message={wheelStatus.message}
                    tone={wheelStatus.tone}
                    iconSrc={wheelStatus.iconSrc}
                    iconAlt={wheelStatus.iconAlt}
                  />
                  <div className="giveaway-submit-row">
                    <WinnersPanel
                      winners={winners}
                      nameSuggestions={names}
                      assigningWinnerId={assigningWinnerId}
                      onAddWinner={addWinnerEntry}
                      onStartAssignItem={handleStartAssignWinnerItem}
                      onCancelAssignItem={handleCancelAssignWinnerItem}
                      onRemoveWinner={handleRemoveWinnerEntry}
                      onRemoveWinnerItem={handleRemoveWinnerItem}
                      disabled={loading}
                    />
                    {canSubmitGiveaway && (
                      <PostGiveaway
                        disabled={loading || winners.length === 0}
                        isBusy={isSubmittingGiveaway}
                        onSubmit={handleSubmitGiveaway}
                        statusMessage={giveawayStatus.message}
                        statusTone={giveawayStatus.tone}
                      />
                    )}
                  </div>
                  <WinnerMenu
                    winner={currentWinnerName}
                    winnerIconSrc={winnerSnapshot.iconSrc}
                    winnerEmojiSeed={winnerSnapshot.emojiSeed}
                    showQuagganEmojis={Boolean(settings?.showQuagganEmojis)}
                    winnerInNames={isCurrentWinnerInNames}
                    winnerNameRemovedFromList={winnerNameRemovedFromList}
                    onClose={handleCloseWinnerMenu}
                    onSpinAgain={handleSpinAgain}
                    onAddWinner={handleAddWinnerFromMenu}
                    onRemoveWinner={handleRemoveWinnerFromNames}
                    onAddWinnerBack={handleAddWinnerBackToNames}
                    onOpenPublishWindow={handleOpenPublishWindow}
                    visible={showWinnerMenu && Boolean(currentWinnerName)}
                    muted={muted}
                  />
                  <PublishWindow
                    visible={showPublishWindow}
                    onClose={handleClosePublishWindow}
                    bannerTitle="Quaggan Wheel"
                    bannerSubtitle="Fortune"
                    leftGif={DancingPinkQuaggan}
                    rightGif={DancingBlueQuaggan}
                    publishWheelSnapshot={publishWheelSnapshot}
                    getNameIconSrc={getNameIconSrc}
                    showQuagganEmojis={Boolean(settings?.showQuagganEmojis)}
                    winnerSelector='[data-publish-snapshot-root="winner-menu"]'
                  />
                </div>
                <div className="side-panel right-panel">
                  <div className="login-header-row">
                    <button
                      className="names-btn secondary"
                      onClick={() => setShowSimulationTest(true)}
                    >
                      Test
                    </button>
                    <button
                      className="names-btn secondary"
                      onClick={() => setShowMissionSetup(true)}
                    >
                      Mission Setup
                    </button>
                    <p className="login-logged-row">
                      <Link to="/public/history"><strong>Public</strong></Link>
                    </p>
                    <SettingsButton onClick={() => setShowSettings(true)} />
                    <MuteButton muted={muted} setMuted={setMuted} />
                  </div>
                  <LoginForm />
                  <FunPanel
                    onAddRandom={handleAddRandomNonparticipant}
                    onSeeList={() => setIsNonparticipantWindowOpen(true)}
                    disabled={!!spinning || nonparticipants.length === 0}
                  />
                  <NamesPanel
                    names={names}
                    setNames={setNames}
                    getNameEmojiSeed={getNameEmojiSeed}
                    getNameIconSrc={getNameIconSrc}
                    onRerollNameEmoji={rerollNameEmoji}
                    input={input}
                    setInput={setInput}
                    handleAddName={handleAddName}
                    disabled={!!spinning}
                  />
                </div>
              </div>
            </>
          }
        />
        <Route
          path="/register"
          element={
            <div className="register-route">
              <NavigationRow />
              <div className="register-route-inner">
                {showRegisterLogin ? (
                  <LoginForm
                    title="Login to your account"
                    onSuccess={() => navigate("/")}
                    onSwitchToRegister={() => setShowRegisterLogin(false)}
                  />
                ) : (
                  <RegisterForm
                    onSwitchToLogin={() => setShowRegisterLogin(true)}
                  />
                )}
              </div>
            </div>
          }
        />
        <Route path="/game/:id" element={<GamePage />} />
        <Route path="/guildmember/:gw2Username" element={<GuildMemberDetailPage />} />
        <Route path="/admin/*" element={<AdminPage />} />
        <Route path="/public/*" element={<PublicPage />} />
        <Route path="/users/:username" element={<UserPage />} />
        {/* Future routes for account, etc. */}
      </Routes>
      <MissionSetupWindow
        visible={showMissionSetup}
        onClose={() => setShowMissionSetup(false)}
        onPopulate={handlePopulateMissionSetup}
        disabled={Boolean(spinning)}
      />
      <SimulationTestWindow
        visible={showSimulationTest}
        onClose={() => setShowSimulationTest(false)}
        names={names}
        settings={settings}
      />
      <SettingsModal
        visible={showSettings}
        onClose={() => setShowSettings(false)}
      />
    </div>
  );
}

function App() {
  return (
    <>
      <HalloweenTheme />
      <FishSwimmer />
      <FishSwimmer3D />
      <AuthProvider>
        <WheelSettingsProvider>
          <AppContent />
        </WheelSettingsProvider>
      </AuthProvider>
    </>
  );
}

export default App;
